import React, {useEffect, useState} from "react";
import {withStyles} from "@material-ui/core";
import i18n from "../../../i18n";
import DialogTitle from "@material-ui/core/DialogTitle";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import Icon from "@material-ui/core/Icon";
import Divider from "@material-ui/core/Divider";
import DialogContent from "@material-ui/core/DialogContent";
import Dialog from "@material-ui/core/Dialog";
import Button from "@material-ui/core/Button";
import DialogActions from "@material-ui/core/DialogActions";
import FormField from "../../Form/FormField";
import Grid from "@material-ui/core/Grid";
import {getMutationName, getSelectOptionEntity} from "../../Form/utils";
import FormAutocompleteGQL from "../../Form/FormAutocompleteGQL";
import attachmentQueries from "../../../queries/attachmentQueries";
import attachmentMutations from "../../../mutations/attachmentMutations";
import attachmentReferenceMutations from "../../../mutations/attachmentReferenceMutations";
import ReactDOM from 'react-dom';
import swal from "sweetalert";
import SwalList from "../../Helper/SwalList";
import {fileData} from "../../Utilities";

const getFileExtension = (file) => {
    let fileExtension = '';
    file.sName.replace(/\.[^/.]+$/, ( ext => { fileExtension = ext }  ) );
    return fileExtension || file.sExt;
}

const removeFileExtension = (fileName) => {
    return fileName.replace(/\.[^/.]+$/, "");
}

// update alias from attachment reference
const updateFileName = async (values) => {

    if(values.relation) {
        let data = {
            id: parseInt( values.relation.id),
            alias: values.alias || null,
        };
        if(data.id) {
            const mutation = attachmentReferenceMutations.updateAttachmentReference;
            const {data: result} = await window.apolloClient.mutate(
                { mutation, variables: {...data}, errorPolicy: "all"  }
            ).catch((e) => { console.log('graphQLErrors', e ) });
            const queryName = getMutationName(mutation);
            return result[queryName];
        }
    }
    return false;
}

// update type_id from attachment
const updateDocumentType = async (values) => {

    if(values.type) {

        let data = {
            id: parseInt(values.file.iId),
            type_id: values.type || null,
        }
        if(data.id) {
            const mutation = attachmentMutations.updateAttachment;
            const {data: result} = await window.apolloClient.mutate(
                { mutation, variables: {...data}, errorPolicy: "all"  }
            ).catch((e) => { console.log('graphQLErrors', e ) });
            const queryName = getMutationName(mutation);
            return result[queryName];
        }
    }
    return false;
}

const saveChanges = async (data) => {
    return {
        fileNameChanged: data.fileHasChanged && await updateFileName(data),
        docTypeChanged: data.type ? await updateDocumentType(data) : false,
    }
}

const DocumentEditDialog = (props) => {
    const {
        classes,
        dialog,
        closeDialog,
        changeFileData,
    } = props;

    const file = dialog.file && dialog.file;
    const entity = dialog.entity && dialog.entity;

    const [fileName, setFileName] = useState( '' );
    const [docType, setDocType] = useState( null );
    const [docTypeId, setDocTypeId] = useState('');
    const [fileHasChanged,setFileHasChanged] = useState(false);

    const changeFileName = e => setFileName(e);
    const changeDocType = e => setDocType(e);
    const changeDocTypeId = e => setDocTypeId(e);

    useEffect(() => {
        file && file.oType && changeDocType(file.oType);
    },[ file ] );

    const handleSubmit = () => {
        let ext = getFileExtension(file);

        saveChanges({
            alias: fileName ? fileName + ext : '',
            type: docTypeId,
            file:file,
            relation: fileData(file,{label:entity.label , id: entity.id } )['relation'] || null,
            fileHasChanged: fileHasChanged,
        }).then(data => {

            let messages = [];

            if(data.fileNameChanged || data.docTypeChanged) {
                messages.push(i18n.get('deficiency.file.updated'));
            } else {
                messages.push(i18n.get('deficiency.file.not_updated'));
            }

            // attachment_relations alias
            if(data.fileNameChanged) {
                let name = data.fileNameChanged.alias;
                //let changedName = typeof name === 'string' ? name.alias : name.toString();
                if(name === null) {
                    messages.push(i18n.get('deficiency.file.name_reset',{fileName: file.sName}))
                } else {
                    messages.push(i18n.get('deficiency.file.name_changed',{fileName: name}))
                }

            }
            // attachment type_id
            if(data.docTypeChanged && docType) {
                messages.push(i18n.get('deficiency.file.type_changed',{docType: docType.name}))
            }

            // renders a list in swal
            let wrapper = document.createElement('div');
            ReactDOM.render(<SwalList values={{messages:messages}} />, wrapper);
            let content = wrapper.firstChild;

            closeDialog();
            return swal({
                content: content,
                icon: 'success',
                timer: 4000,
            }).then(() =>  {
                changeFileData({
                    rId: data.fileNameChanged ? parseInt(data.fileNameChanged.id) : null,
                    iId:file.iId,
                    sName:data.fileNameChanged ? data.fileNameChanged.alias : null,
                    docType:docType || null,
                });
                setFileHasChanged(false);
            });
        })
    }

    return (
        <Dialog
            open={dialog.open}
            onClose={(e)=> {console.log('is_closing',e.target)}}
            disableBackdropClick={true}
            className={classes.dialog}
            fullWidth={true}
            maxWidth={'sm'}
            aria-labelledby="form-dialog-title"
        >
            <DialogTitle id="form-dialog-title" className={classes.dialogTitle}  disableTypography>
                <Typography variant="h6" color={'primary'}>{i18n.get('deficiency.file.edit_document')}</Typography>
                <IconButton
                    aria-label="close"
                    className={classes.closeButton}
                    onClick={() => {
                        changeDocType(null);
                        changeFileName('');
                        closeDialog();
                    }}
                >
                    <Icon>close</Icon>
                </IconButton>
                <div className={classes.hints}>{i18n.get('deficiency.file.hints')}</div>
                <div className={classes.hints}>- {i18n.get('deficiency.file.hint_file_extension')}</div>
                <div className={classes.hints}>- {i18n.get('deficiency.file.hint_file_reset')}</div>
            </DialogTitle>
            <Divider/>
            <DialogContent className={classes.dialogContent}>
                <FormField
                    name={'sName'}
                    label={i18n.get('deficiency.file.change_name')}
                    defaultValue={file && removeFileExtension( fileData(file,entity)['name'] )}
                    onBlur={e => {
                        if(e.target.value !== fileData(file,entity)) {
                            setFileHasChanged(true);
                        }
                        changeFileName(e.target.value)
                    }}
                />
                <FormAutocompleteGQL
                    query={attachmentQueries.allAttachmentTypes}
                    labelName={'name'}
                    valueName={'id'}
                    name="type_id"
                    label={i18n.get('deficiency.file.change_doc_type')}
                    onChange={selectedOption => {
                        changeDocType(selectedOption && selectedOption.entity);
                        changeDocTypeId(selectedOption && selectedOption.value);
                    }}
                    defaultOptions
                    defaultValue={docType && getSelectOptionEntity(docType)}
                    isClearable={true}
                />
                {/** client should say, if the doc type is changeable*/}
                {/*file && !file.oType && (
                    <FormAutocompleteGQL
                        query={attachmentQueries.allAttachmentTypes}
                        labelName={'name'}
                        valueName={'id'}
                        name="type_id"
                        label={i18n.get('deficiency.file.change_doc_type')}
                        onChange={selectedOption => {
                            changeDocType(selectedOption && selectedOption.label);
                            changeDocTypeId(selectedOption && selectedOption.value);
                        }}
                        defaultOptions
                        defaultValue={docType}
                        isClearable={true}
                    />
                )*/}

                <Grid container direction={'column'} justify={'flex-start'} alignItems={'flex-start'} className={classes.attributes}>
                    {file && (file.sName.normalize() !== fileName.normalize()) &&
                        <Grid item>
                            <Typography component={'h6'}>
                                Dateiname (original): {file && file.sName}
                            </Typography>
                        </Grid>
                    }
                    <Grid item>
                        <Typography component={'h6'}>
                            Dateityp: {file && file.sMime}
                        </Typography>
                    </Grid>
                    <Grid item>
                        <Typography component={'h6'}>
                            Dokumenttyp: { (docType && docType.name) || 'nicht festgelegt' }
                        </Typography>
                    </Grid>
                    {file && file.aRelations && file.aRelations.length && (
                        <Grid item>
                            <Grid container direction={'row'} justify={'flex-start'} alignItems={'flex-start'}>
                                <Grid item>
                                    <Typography component={'h6'}>
                                        andere Relationen:
                                    </Typography>
                                </Grid>
                                <Grid item>
                                    <Grid container direction={'column'} style={{marginLeft:10}}>
                                        {file.aRelations.length > 1
                                            ? file.aRelations.map(rel =>
                                                rel[entity.label] !== entity.id && (
                                                    <Grid item key={`rel_${rel.id}`}>
                                                        <Typography component={'h6'}>
                                                            - {rel.name}
                                                        </Typography>
                                                    </Grid>
                                                ))
                                            :   <Grid item>
                                                    <Typography component={'h6'}>{'nein'}</Typography>
                                                </Grid>
                                        }
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    )}
                </Grid>
            </DialogContent>
            <DialogActions className={classes.dialogActions}>
                <Button
                    component={'a'}
                    variant={'contained'}
                    color={'primary'}
                    size={'small'}
                    onClick={handleSubmit}
                >{i18n.get('default.save')}</Button>
            </DialogActions>
        </Dialog>
    );
}

const styles = theme => ({
    dialog: {
        minHeight:700,
    },
    dialogTitle: {
        color: theme.colors.primary,
        "& h6":{
            color: theme.colors.primary,
        },
    },
    dialogContent: {
        overflow:'visible',
    },
    dialogActions: {
        paddingTop: theme.spacing.unit * 2,
        paddingRight: theme.spacing.unit * 2,
        paddingBottom: theme.spacing.unit * 2,
    },
    closeButton: {
        position: 'absolute',
        right: theme.spacing.unit * 1.2,
        top: theme.spacing.unit * 2,
    },
    textArea: {
        padding:'0 !important',
        lineHeight: 1.5,
    },
    attributes: {
        opacity:0.6,
        paddingTop: theme.spacing.unit * 2,
    },
    hints: {
        fontSize:12,
        color:'#000000',
        opacity:0.75,
    },
});

export default withStyles(styles)(DocumentEditDialog);

